/* global HTMLDivElement, KeyboardEvent, MouseEvent, Node */
/**
 * NlSyntaxHint — popover listing NL quick-add token examples.
 * Pure presentational; toggled by a "?" button next to the capture input.
 * Closes on outside click or Esc key.
 */
import { useEffect, useRef } from "react";
import { HelpCircle, X } from "lucide-react";

// ── Token row data ────────────────────────────────────────────────────────────

interface TokenGroup {
  category: string;
  rows: { token: string; hint: string }[];
}

const TOKEN_GROUPS: TokenGroup[] = [
  {
    category: "Ngày",
    rows: [
      { token: "hôm nay", hint: "hôm nay" },
      { token: "mai",     hint: "ngày mai" },
      { token: "mốt",     hint: "ngày kia" },
      { token: "thứ 6",   hint: "thứ Sáu tuần này (hoặc tuần sau)" },
    ],
  },
  {
    category: "Giờ",
    rows: [
      { token: "3pm",  hint: "15:00" },
      { token: "15h",  hint: "15:00 (định dạng VN)" },
      { token: "9h30", hint: "09:30" },
    ],
  },
  {
    category: "Ưu tiên",
    rows: [
      { token: "!cao",  hint: "ưu tiên cao" },
      { token: "!tb",   hint: "ưu tiên trung bình" },
      { token: "!thấp", hint: "ưu tiên thấp" },
    ],
  },
  {
    category: "Nhãn",
    rows: [
      { token: "#tag", hint: "gắn nhãn bất kỳ, VD: #work #gia-đình" },
    ],
  },
];

const EXAMPLE_LINE = '"họp 3pm mai #work !cao"';

// ── Props ─────────────────────────────────────────────────────────────────────

interface NlSyntaxHintProps {
  open: boolean;
  onClose: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function NlSyntaxHint({ open, onClose }: NlSyntaxHintProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Delay one tick so the toggle click itself doesn't immediately close
    const id = setTimeout(() => document.addEventListener("mousedown", handler), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", handler);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="Cú pháp ghi nhanh"
      className="absolute left-0 top-full z-40 mt-2 w-72 rounded-2xl border border-ink-200 bg-white p-4 shadow-xl"
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-1.5 font-display text-[13px] font-bold text-ink-800">
          <HelpCircle className="h-3.5 w-3.5 text-brand-500" />
          Cú pháp ghi nhanh
        </span>
        <button
          onClick={onClose}
          aria-label="Đóng gợi ý"
          className="grid h-5 w-5 place-items-center rounded-md text-ink-400 hover:bg-ink-100 hover:text-ink-700"
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      {/* Token groups */}
      <div className="space-y-3">
        {TOKEN_GROUPS.map((group) => (
          <div key={group.category}>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-ink-400">
              {group.category}
            </p>
            <div className="space-y-0.5">
              {group.rows.map((row) => (
                <div key={row.token} className="flex items-baseline gap-2">
                  <code className="shrink-0 rounded bg-brand-50 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-brand-700">
                    {row.token}
                  </code>
                  <span className="text-[11px] text-ink-500">{row.hint}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Example line */}
      <div className="mt-3 rounded-xl bg-ink-50 px-3 py-2">
        <p className="text-[10px] font-bold uppercase tracking-widest text-ink-400">Ví dụ</p>
        <p className="mt-0.5 font-mono text-[12px] text-ink-700">{EXAMPLE_LINE}</p>
      </div>
    </div>
  );
}
